import { useEffect, useState } from "react";

import {
  IS_WEB_VIEW,
  sendMessageToReactNativeWebView,
  WEB_VIEW_ACTIONS,
} from "@/shared/lib/";
import type { WebViewMessage } from "@/shared/lib/";
import { AVAILABLE_LANGUAGE, availableLanguageSchema } from "@/shared/models";
import { authStore, useSettingsStore } from "@/shared/stores";
import { useKeyboardDetection } from "./use-keyboard-detection";

export const useWebViewAuth = () => {
  const [isLoading, setIsLoading] = useState(true);
  useKeyboardDetection();
  const setLanguage = useSettingsStore((state) => state.setLanguage);
  const setIsBiometricEnabled = useSettingsStore(
    (state) => state.setIsBiometricEnabled,
  );

  useEffect(() => {
    if (!IS_WEB_VIEW) {
      setIsLoading(false);
      return;
    }

    const handleMessage = (event: MessageEvent<WebViewMessage>) => {
      try {
        if (typeof event.data !== "string") {
          return;
        }

        const data = JSON.parse(event.data) as WebViewMessage;

        if (data.type === WEB_VIEW_ACTIONS.SET_TOKEN) {
          const token = String(data.payload?.token);
          authStore.setToken(token);
          setIsLoading(false);
        }

        if (data.type === WEB_VIEW_ACTIONS.SET_LANGUAGE) {
          const languageParseResult = availableLanguageSchema.safeParse(
            data.payload?.language,
          );

          if (languageParseResult.success) {
            setLanguage(languageParseResult.data);
          } else {
            setLanguage(AVAILABLE_LANGUAGE.EN);
          }
        }

        if (data.type === WEB_VIEW_ACTIONS.SET_IS_BIOMETRIC_ENABLED) {
          const isBiometricEnabled = Boolean(data.payload?.checked);

          setIsBiometricEnabled(isBiometricEnabled);
        }
      } catch (error) {
        console.warn("Failed to parse WebView message:", error);
      }
    };

    // IMPORTANT: window.addEventListener is used for iOS and document.addEventListener is used for Android
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    sendMessageToReactNativeWebView({ type: WEB_VIEW_ACTIONS.REQUEST_TOKEN });

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, [setLanguage, setIsBiometricEnabled]);

  return { isLoading };
};
